import React, { useState, useEffect, useCallback } from 'react';
import { ArrowRight, Calendar } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../context/LanguageContext';
import { getBlogPosts } from '../services/api';

const BlogSection = () => {
  const { language, t } = useLanguage();
  const [blogPosts, setBlogPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const loadBlogPosts = useCallback(async () => {
    try {
      const posts = await getBlogPosts(null, 4);
      setBlogPosts(posts);
    } catch (err) {
      // Silently fail - will show empty state
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBlogPosts();
  }, [loadBlogPosts]);

  // Helper function to get translated content
  const getTranslated = (post, field) => {
    if (language === 'es' && post[`${field}Es`]) {
      return post[`${field}Es`];
    }
    if (language === 'fr' && post[`${field}Fr`]) {
      return post[`${field}Fr`];
    }
    return post[field];
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  if (isLoading) {
    return (
      <section id="blog" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="h-8 bg-gray-200 rounded w-32 mx-auto mb-4 animate-pulse"></div>
            <div className="h-12 bg-gray-200 rounded w-96 mx-auto mb-4 animate-pulse"></div>
            <div className="h-6 bg-gray-200 rounded w-64 mx-auto animate-pulse"></div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-gray-100 rounded-2xl h-64 animate-pulse"></div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="blog" className="py-24 bg-white" aria-label="Discover Puerto Plata Blog">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section header */}
        <div className="text-center mb-16">
          <span className="inline-block text-[#000080] bg-[#FFFF00] font-semibold tracking-wider uppercase text-sm mb-4 px-4 py-1 rounded-full">
            {t('blog.badge')}
          </span>
          <h2 className="font-display text-4xl md:text-5xl font-bold text-[#000080] mb-6">
            {t('blog.title')}
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            {t('blog.subtitle')}
          </p>
        </div>

        {/* Blog grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {blogPosts.map((post, index) => {
            const categoryColors = ['bg-[#FF6B35]', 'bg-[#22C55E]', 'bg-[#8B5CF6]', 'bg-[#00BFFF]'];
            return (
            <article
              key={post.id}
              className={`blog-card card-tropical bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-100 ${
                index === 0 ? 'md:col-span-2 md:row-span-2' : ''
              }`}
            >
              {/* Image */}
              <div className={`blog-image-wrapper relative ${
                index === 0 ? 'h-64 md:h-80' : 'h-48'
              }`}>
                <img
                  src={post.image}
                  alt={getTranslated(post, 'title')}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-4 left-4">
                  <span className={`${categoryColors[index % 4]} text-white text-xs font-bold px-3 py-1 rounded-full`}>
                    {getTranslated(post, 'category')}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="flex items-center gap-2 text-gray-400 text-sm mb-3">
                  <Calendar className="w-4 h-4" />
                  {formatDate(post.date)}
                </div>

                <h3 className={`font-display font-bold text-[#000080] mb-3 hover:text-[#8B5CF6] transition-colors cursor-pointer ${
                  index === 0 ? 'text-2xl' : 'text-lg'
                }`}>
                  {getTranslated(post, 'title')}
                </h3>

                <p className={`text-gray-600 mb-4 ${
                  index === 0 ? 'text-base' : 'text-sm line-clamp-2'
                }`}>
                  {getTranslated(post, 'excerpt')}
                </p>

                <button className="flex items-center gap-2 text-[#FF6B35] font-semibold hover:text-[#E85A2A] transition-colors group">
                  {t('blog.readMore')}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </article>
          )})}
        </div>

        {/* View all button */}
        <div className="text-center mt-12">
          <Button
            variant="outline"
            className="border-2 border-[#8B5CF6] text-[#8B5CF6] hover:bg-[#8B5CF6] hover:text-white font-semibold px-8 py-6"
          >
            {t('blog.viewAll')}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default BlogSection;
